
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { StudentRequest } from "../hooks/useStudentRequests";
import { Button } from "@/components/ui/button";
import { Trash, UserPlus } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { StudentRequestDialog } from "../StudentRequestDialog";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";

interface RequestsTableProps {
  filteredRequests: StudentRequest[] | undefined;
  onDeleteRequest: (id: string) => Promise<boolean>;
}

export const RequestsTable = ({ 
  filteredRequests,
  onDeleteRequest
}: RequestsTableProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<StudentRequest | null>(null);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslations("students");
  const { getPath } = useLanguagePath();
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">
          {language === 'pt' ? 'Pendente' : 'Pending'}
        </Badge>;
      case "approved":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">
          {language === 'pt' ? 'Aprovado' : 'Approved'}
        </Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700">
          {language === 'pt' ? 'Rejeitado' : 'Rejected'}
        </Badge>;
      default:
        return <Badge variant="outline" className="dark:text-gray-300">
          {language === 'pt' ? 'Desconhecido' : 'Unknown'}
        </Badge>;
    }
  };

  const handleDeleteClick = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Previne que o clique na linha abra o diálogo
    }
    console.log("Delete button clicked for ID:", id);
    setSelectedRequestId(id);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedRequestId && !isDeleting) {
      setIsDeleting(true);
      console.log("Confirming delete for ID:", selectedRequestId);
      
      try {
        const success = await onDeleteRequest(selectedRequestId);
        if (success) {
          console.log("Delete successful, closing dialog");
        }
      } finally {
        setIsDeleteDialogOpen(false);
        setSelectedRequestId(null);
        setIsDeleting(false);
        setSelectedRequest(null);
      }
    }
  };

  const handleCreateStudent = (request: StudentRequest, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Previne que o clique na linha abra o diálogo
    }
    navigate(getPath("/dashboard/students/new"), { 
      state: {
        first_name: request.first_name,
        last_name: request.last_name,
        email: request.email,
        phone: request.phone,
        goals: request.goals
      }
    });
  };

  const handleRowClick = (request: StudentRequest) => {
    setSelectedRequest(request);
  };

  const closeRequestDialog = () => {
    setSelectedRequest(null);
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="dark:text-white">{t('table.name')}</TableHead>
            <TableHead className="hidden sm:table-cell dark:text-white">Email</TableHead>
            <TableHead className="hidden sm:table-cell dark:text-white">{t('table.phone')}</TableHead>
            <TableHead className="dark:text-white">{language === 'pt' ? 'Data' : 'Date'}</TableHead>
            <TableHead className="dark:text-white">{t('table.status')}</TableHead>
            <TableHead className="text-right dark:text-white">{t('table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredRequests?.map(request => (
            <TableRow 
              key={request.id} 
              className="hover:bg-slate-50/50 dark:hover:bg-gray-700/30 cursor-pointer"
              onClick={() => handleRowClick(request)}
            >
              <TableCell className="font-medium dark:text-gray-300">
                {request.first_name} {request.last_name}
              </TableCell>
              <TableCell className="hidden sm:table-cell dark:text-gray-300">
                {request.email}
              </TableCell>
              <TableCell className="hidden sm:table-cell dark:text-gray-300">
                {request.phone || (language === 'pt' ? "Não fornecido" : "Not provided")}
              </TableCell>
              <TableCell className="dark:text-gray-300">
                {request.created_at && format(new Date(request.created_at), 'dd/MM/yyyy')}
              </TableCell>
              <TableCell>
                {getStatusBadge(request.status)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => handleCreateStudent(request, e)}
                    className="h-8 w-8 p-0"
                    title={t('studentRequests.createStudent')}
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => handleDeleteClick(request.id, e)}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    title={t('studentRequests.deleteRequest')}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {filteredRequests?.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-4 dark:text-gray-300">
                {t('studentRequests.noRequestsFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {selectedRequest && (
        <StudentRequestDialog
          request={selectedRequest}
          onClose={closeRequestDialog}
          onReject={() => handleDeleteClick(selectedRequest.id)}
          onCreateStudent={() => handleCreateStudent(selectedRequest)}
        />
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="dark:bg-gray-800 dark:border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">{t('studentRequests.deleteDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-300">
              {t('studentRequests.deleteConfirmation')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} className="dark:text-white dark:bg-gray-700 dark:hover:bg-gray-600">
              {t('studentRequests.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600"
              disabled={isDeleting}
            >
              {isDeleting ? t('studentRequests.deleting') : t('studentRequests.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
